import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, RefreshCw, AlertTriangle, CheckCircle, Clock, Truck, Camera, Eye, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { useVehiclesStore } from '@/store/vehiclesStore';
import { apiUtils } from '@/services/api';
import VehicleFormDialog from './VehicleFormDialog';
import VehicleDetailsDialog from './VehicleDetailsDialog';
import VehicleFilters from './VehicleFilters';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';

const VehicleManagement = ({ transporterId, onStatisticsUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    vehicle: null,
    isLoading: false
  });

  const {
    vehicles,
    vehiclesLoading,
    vehiclesError,
    vehicleStatistics,
    filters,
    loadVehiclesByTransporter,
    createVehicle,
    updateVehicle,
    deleteVehicle,
    updateFilters,
    clearErrors,
    getFormattedVehicles,
  } = useVehiclesStore();

  // Charger les données au montage
  useEffect(() => {
    if (transporterId) {
      loadInitialData();
    }
  }, [transporterId]);

  // Charger les données initiales
  const loadInitialData = async () => {
    try {
      await loadVehiclesByTransporter(transporterId);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les données des véhicules',
        variant: 'destructive',
      });
    }
  };

  // Gestion de la recherche
  const handleSearch = (value) => {
    setSearchTerm(value);
    updateFilters({ search: value });
  };

  // Gestion des filtres
  const handleFiltersChange = (newFilters) => {
    updateFilters(newFilters);
  };

  // Rafraîchir les données
  const handleRefresh = async () => {
    try {
      await loadVehiclesByTransporter(transporterId, filters);
      toast({
        title: 'Succès',
        description: 'Données mises à jour',
      });
    } catch (error) {
      console.error('Erreur lors du rafraîchissement:', error);
    }
  };

  // Ouvrir le formulaire d'ajout
  const handleAddVehicle = () => {
    setEditingVehicle(null);
    setIsFormOpen(true);
  };

  // Ouvrir le formulaire d'édition
  const handleEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setIsFormOpen(true);
  };

  // Ouvrir les détails
  const handleViewVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setIsDetailsOpen(true);
  };

  // Soumettre le formulaire
  const handleSubmit = async (vehicleData) => {
    try {
      const dataWithTransporter = {
        ...vehicleData,
        transporter: transporterId,
      };

      if (editingVehicle) {
        await updateVehicle(editingVehicle.id, dataWithTransporter);
        toast({
          title: 'Succès',
          description: 'Véhicule modifié avec succès',
        });
      } else {
        await createVehicle(dataWithTransporter);
        toast({
          title: 'Succès',
          description: 'Véhicule ajouté avec succès',
        });
      }

      setIsFormOpen(false);
      setEditingVehicle(null);
      
      // Rafraîchir les données
      await loadVehiclesByTransporter(transporterId, filters);
      
      // Notifier le parent pour mettre à jour les statistiques
      if (onStatisticsUpdate) {
        onStatisticsUpdate();
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      if (error.type === 'validation') {
        // Les erreurs de validation sont gérées par le formulaire
        throw error;
      } else {
        // Afficher un toast seulement pour les erreurs non-validation
        toast({
          title: 'Erreur',
          description: error.message || 'Impossible de sauvegarder le véhicule',
          variant: 'destructive',
        });
        throw error; // Relancer l'erreur pour que le formulaire reste ouvert
      }
    }
  };

  // Sauvegarder un véhicule
  const handleSaveVehicle = async (vehicleData) => {
    try {
      if (editingVehicle) {
        await updateVehicle(editingVehicle.id, vehicleData);
        toast({
          title: 'Succès',
          description: 'Véhicule modifié avec succès',
        });
      } else {
        await createVehicle(vehicleData);
        toast({
          title: 'Succès',
          description: 'Véhicule créé avec succès',
        });
      }
      
      // Fermer le formulaire
      setIsFormOpen(false);
      setEditingVehicle(null);
      
      // Rafraîchir les données
      await loadVehiclesByTransporter(transporterId, filters);
      
      // Notifier le parent pour mettre à jour les statistiques
      if (onStatisticsUpdate) {
        onStatisticsUpdate();
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      if (error.type === 'validation') {
        // Les erreurs de validation sont gérées par le formulaire
        throw error;
      } else {
        // Afficher un toast seulement pour les erreurs non-validation
        toast({
          title: 'Erreur',
          description: error.message || 'Impossible de sauvegarder le véhicule',
          variant: 'destructive',
        });
        throw error; // Relancer l'erreur pour que le formulaire reste ouvert
      }
    }
  };

  // Supprimer un véhicule
  const handleDeleteVehicle = (vehicle) => {
    setDeleteConfirmation({
      isOpen: true,
      vehicle: vehicle,
      isLoading: false
    });
  };

  // Confirmer la suppression
  const confirmDeleteVehicle = async () => {
    if (!deleteConfirmation.vehicle) return;

    setDeleteConfirmation(prev => ({ ...prev, isLoading: true }));

    try {
      await deleteVehicle(deleteConfirmation.vehicle.id);
      toast({
        title: 'Succès',
        description: 'Véhicule supprimé avec succès',
      });
      
      // Rafraîchir les données
      await loadVehiclesByTransporter(transporterId, filters);
      
      // Notifier le parent pour mettre à jour les statistiques
      if (onStatisticsUpdate) {
        onStatisticsUpdate();
      }

      // Fermer la modale
      setDeleteConfirmation({
        isOpen: false,
        vehicle: null,
        isLoading: false
      });
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de supprimer le véhicule',
        variant: 'destructive',
      });
      setDeleteConfirmation(prev => ({ ...prev, isLoading: false }));
    }
  };

  // Annuler la suppression
  const cancelDeleteVehicle = () => {
    setDeleteConfirmation({
      isOpen: false,
      vehicle: null,
      isLoading: false
    });
  };

  // Obtenir la couleur du statut
  const getStatusColor = (status) => {
    const colors = {
      'AVAILABLE': 'bg-green-500/20 text-green-400 border-green-500/30',
      'IN_USE': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'MAINTENANCE': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'OUT_OF_SERVICE': 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  // Obtenir la couleur du statut de l'assurance
  const getInsuranceColor = (status) => {
    const colors = {
      'valid': 'text-green-500',
      'expiring_soon': 'text-orange-500',
      'expired': 'text-red-500',
      'unknown': 'text-gray-500',
    };
    return colors[status] || 'text-gray-500';
  };

  // Obtenir la couleur du statut du contrôle technique
  const getTechnicalColor = (status) => {
    const colors = {
      'valid': 'text-green-500',
      'expiring_soon': 'text-orange-500',
      'expired': 'text-red-500',
      'unknown': 'text-gray-500',
    };
    return colors[status] || 'text-gray-500';
  };

  // Obtenir l'icône du statut
  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid':
        return <CheckCircle className="w-4 h-4" />;
      case 'expiring_soon':
        return <Clock className="w-4 h-4" />;
      case 'expired':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const formattedVehicles = getFormattedVehicles();

  return (
    <div className="space-y-6">
      {/* En-tête avec actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Gestion des Véhicules</h3>
          <p className="text-sm text-muted-foreground">
            {vehicleStatistics?.total || 0} véhicule(s) enregistré(s)
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            onClick={handleAddVehicle}
            className="bg-green-600 hover:bg-green-700"
            disabled={vehiclesLoading}
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un véhicule
          </Button>
          
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={vehiclesLoading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${vehiclesLoading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Rechercher par plaque, marque, modèle..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className="w-full sm:w-auto"
        >
          <Filter className="w-4 h-4 mr-2" />
          Filtres
        </Button>
      </div>

      {/* Filtres avancés */}
      {showFilters && (
        <VehicleFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Statistiques rapides */}
      {vehicleStatistics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card key="available">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-muted-foreground">Disponibles</span>
              </div>
              <p className="text-2xl font-bold">{vehicleStatistics.by_status?.AVAILABLE || 0}</p>
            </CardContent>
          </Card>
          
          <Card key="in-use">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-muted-foreground">En utilisation</span>
              </div>
              <p className="text-2xl font-bold">{vehicleStatistics.by_status?.IN_USE || 0}</p>
            </CardContent>
          </Card>
          
          <Card key="maintenance">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-500" />
                <span className="text-sm text-muted-foreground">Maintenance</span>
              </div>
              <p className="text-2xl font-bold">{vehicleStatistics.by_status?.MAINTENANCE || 0}</p>
            </CardContent>
          </Card>
          
          <Card key="out-of-service">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <span className="text-sm text-muted-foreground">Hors service</span>
              </div>
              <p className="text-2xl font-bold">{vehicleStatistics.by_status?.OUT_OF_SERVICE || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Liste des véhicules */}
      {vehiclesLoading ? (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Chargement des véhicules...</span>
        </div>
      ) : vehiclesError ? (
        <Card>
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-500 mb-2">Erreur lors du chargement</p>
            <p className="text-sm text-muted-foreground mb-4">{vehiclesError.message}</p>
            <Button onClick={handleRefresh} variant="outline">
              Réessayer
            </Button>
          </CardContent>
        </Card>
      ) : formattedVehicles.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Aucun véhicule</h3>
            <p className="text-muted-foreground mb-4">
              Commencez par ajouter votre premier véhicule
            </p>
            <Button onClick={handleAddVehicle} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un véhicule
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {formattedVehicles.map((vehicle) => (
            <Card key={vehicle.id} className="hover:shadow-lg transition-all duration-200 overflow-hidden">
              <CardContent className="p-0">
                <div className="flex flex-col lg:flex-row">
                  {/* Image du véhicule */}
                  <div className="lg:w-80 h-48 lg:h-auto bg-muted flex items-center justify-center relative">
                    {vehicle.image ? (
                      <img
                        src={vehicle.image}
                        alt={`${vehicle.make} ${vehicle.model}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-muted-foreground">
                        <Truck className="w-12 h-12 mb-2" />
                        <span className="text-sm">Aucune image</span>
                      </div>
                    )}
                    
                    {/* Badge de statut sur l'image */}
                    <div className="absolute top-3 left-3">
                      <Badge className={getStatusColor(vehicle.status)}>
                        {vehicle.status_display}
                      </Badge>
                    </div>
                  </div>

                  {/* Contenu principal */}
                  <div className="flex-1 p-6">
                    <div className="flex flex-col h-full">
                      {/* En-tête avec plaque et actions */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-xl font-bold text-foreground">
                              {vehicle.plate_number}
                            </h3>
                            {vehicle.logiconnect_id && (
                              <LogiConnectIdBadge 
                                id={vehicle.logiconnect_id} 
                                showLabel={false}
                                size="sm"
                              />
                            )}
                          </div>
                          <p className="text-lg text-muted-foreground">
                            {vehicle.make} {vehicle.model} ({vehicle.year})
                          </p>
                        </div>
                        
                        {/* Actions rapides */}
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewVehicle(vehicle)}
                            className="flex items-center gap-1"
                          >
                            <Eye className="w-4 h-4" />
                            Voir
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditVehicle(vehicle)}
                            className="flex items-center gap-1"
                          >
                            <Edit className="w-4 h-4" />
                            Modifier
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteVehicle(vehicle)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 flex items-center gap-1"
                          >
                            <Trash2 className="w-4 h-4" />
                            Supprimer
                          </Button>
                        </div>
                      </div>

                      {/* Informations détaillées */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Type:</span>
                            <span className="font-medium">{vehicle.vehicle_type_label}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Capacité:</span>
                            <span className="font-medium">
                              {vehicle.capacity_volume ? `${vehicle.capacity_volume} m³` : 'N/A'} / 
                              {vehicle.capacity_weight ? `${vehicle.capacity_weight} kg` : 'N/A'}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-2">
                          {/* Informations supplémentaires */}
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">ID:</span>
                            <span className="font-mono text-xs">{vehicle.id}</span>
                          </div>
                        </div>
                      </div>

                      {/* Statuts d'assurance et contrôle technique */}
                      <div className="flex items-center gap-6 mt-auto">
                        <div className={`flex items-center gap-2 ${getInsuranceColor(vehicle.insurance_status)}`}>
                          {getStatusIcon(vehicle.insurance_status)}
                          <span className="text-sm font-medium">Assurance</span>
                        </div>
                        <div className={`flex items-center gap-2 ${getTechnicalColor(vehicle.technical_control_status)}`}>
                          {getStatusIcon(vehicle.technical_control_status)}
                          <span className="text-sm font-medium">Contrôle technique</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modals */}
      <VehicleFormDialog
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingVehicle(null);
        }}
        onSubmit={handleSaveVehicle}
        vehicle={editingVehicle}
        transporterId={transporterId}
      />

      <VehicleDetailsDialog
        isOpen={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false);
          setSelectedVehicle(null);
        }}
        vehicle={selectedVehicle}
        onEdit={handleEditVehicle}
        onDelete={handleDeleteVehicle}
      />

      {/* Modale de confirmation de suppression */}
      <ConfirmationDialog
        open={deleteConfirmation.isOpen}
        onOpenChange={(open) => {
          if (!open) cancelDeleteVehicle();
        }}
        title="Supprimer le véhicule"
        description={`Êtes-vous sûr de vouloir supprimer le véhicule ${deleteConfirmation.vehicle?.plate_number} ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="destructive"
        onConfirm={confirmDeleteVehicle}
        onCancel={cancelDeleteVehicle}
        isLoading={deleteConfirmation.isLoading}
      >
        {deleteConfirmation.vehicle && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-red-800 dark:text-red-200">
                  Véhicule à supprimer :
                </p>
                <p className="text-red-700 dark:text-red-300 mt-1">
                  <strong>{deleteConfirmation.vehicle.plate_number}</strong> - {deleteConfirmation.vehicle.make} {deleteConfirmation.vehicle.model}
                </p>
                <p className="text-red-600 dark:text-red-400 mt-2">
                  Toutes les données associées à ce véhicule seront également supprimées.
                </p>
              </div>
            </div>
          </div>
        )}
      </ConfirmationDialog>
    </div>
  );
};

export default VehicleManagement;